import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaQrcode,
  FaPrint,
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaCheckCircle,
  FaRegCircle,
  FaTrophy,
  FaClock,
  FaPlus,
  FaTrash,
  FaPen,
  FaTimes,
  FaSave,
} from "react-icons/fa";
import { jsPDF } from "jspdf";
import dayjs from "dayjs";

import HeaderContainer from "../CrewDashboard/HeaderContainer";
import { useReportConditions } from "../../hooks/queries";
import { supabase } from "../../supabaseClient";
import actLogo from "../../images/act-logo.png";
import { formatInAirportTz, localHourMinute } from "../../utils/airportTz";

const CREW_PORTAL_BASE_URL = "https://crew.aircrewtransport.com";

// Each manager gets their own crew-portal URL. The portal looks up the
// manager by UserID and shows only that manager's schedules — so the QR is
// effectively scoped to whichever location the manager is assigned to.
const portalUrlForManager = (userId) =>
  `${CREW_PORTAL_BASE_URL}/m/${userId}`;

// Stable colors per airport code so the same code always renders the same hue.
const AIRPORT_PALETTE = [
  { bg: "#fee2e2", fg: "#b91c1c" }, // red
  { bg: "#fed7aa", fg: "#c2410c" }, // orange
  { bg: "#fef3c7", fg: "#a16207" }, // amber
  { bg: "#d1fae5", fg: "#047857" }, // emerald
  { bg: "#cffafe", fg: "#0e7490" }, // cyan
  { bg: "#dbeafe", fg: "#1d4ed8" }, // blue
  { bg: "#e0e7ff", fg: "#4338ca" }, // indigo
  { bg: "#ede9fe", fg: "#6d28d9" }, // violet
  { bg: "#fae8ff", fg: "#a21caf" }, // fuchsia
  { bg: "#fce7f3", fg: "#be185d" }, // pink
];

const colorForCode = (code) => {
  if (!code) return { bg: "#e5e7eb", fg: "#374151" };
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return AIRPORT_PALETTE[h % AIRPORT_PALETTE.length];
};

const AirportPill = ({ code }) => {
  const { bg, fg } = colorForCode(code);
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-bold tracking-wide"
      style={{ backgroundColor: bg, color: fg }}
    >
      {code}
    </span>
  );
};

const fetchAsDataURL = (url) =>
  fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error(`QR fetch failed (${r.status})`);
      return r.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

const STORAGE_KEY = "qr-mock-generated-managers";

const Stars = ({ value }) => {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="flex items-center gap-1 text-yellow-500">
      {Array.from({ length: full }).map((_, i) => (
        <FaStar key={`f-${i}`} />
      ))}
      {half && <FaStarHalfAlt />}
      {Array.from({ length: empty }).map((_, i) => (
        <FaRegStar key={`e-${i}`} className="text-gray-300" />
      ))}
      <span className="ml-1 text-sm font-medium text-gray-700">
        {value.toFixed(1)}
      </span>
    </span>
  );
};

const QRCodes = () => {
  const navigate = useNavigate();
  const { data: conditions, isSuccess } = useReportConditions();

  const [generated, setGenerated] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Real aggregates pulled from CrewReviews + CrewScans. Keyed by manager
  // UserID. Empty until the migration has been run / data exists.
  const [scanCounts, setScanCounts] = useState({});
  const [reviewAgg, setReviewAgg] = useState({});

  // When the admin clicks a row's rating, we drill into the individual
  // reviews for that manager. The modal owns its own fetch so we don't keep
  // every review in memory for managers nobody is looking at.
  const [reviewsModal, setReviewsModal] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...generated]));
    } catch {
      // ignore
    }
  }, [generated]);

  useEffect(() => {
    let cancelled = false;

    // Supabase caps a single request at 1000 rows by default, so once the
    // fleet crosses 1000 total scans the top-of-page counter silently
    // freezes at 1000. Page through both tables to get the true totals.
    // We report ALL-TIME scans now — the earlier 30-day rolling window
    // hid growing usage; managers want a running total instead.
    const PAGE = 1000;
    const paginate = async (build) => {
      const all = [];
      for (let off = 0; ; off += PAGE) {
        const { data, error } = await build().range(off, off + PAGE - 1);
        if (error) { console.error("QR aggregates page failed:", error); break; }
        if (!data || data.length === 0) break;
        all.push(...data);
        if (data.length < PAGE) break;
      }
      return all;
    };

    (async () => {
      const [scans, reviews] = await Promise.all([
        paginate(() =>
          supabase
            .from("CrewScans")
            .select("manager_id, scanned_at")
            .order("scanned_at", { ascending: true })
        ),
        paginate(() =>
          supabase
            .from("CrewReviews")
            .select("manager_id, rating")
            .order("id", { ascending: true })
        ),
      ]);

      if (cancelled) return;

      const scanByMgr = {};
      for (const r of scans) {
        scanByMgr[r.manager_id] = (scanByMgr[r.manager_id] || 0) + 1;
      }

      const revByMgr = {};
      for (const r of reviews) {
        const bucket = revByMgr[r.manager_id] || { count: 0, sum: 0 };
        bucket.count += 1;
        bucket.sum += r.rating;
        revByMgr[r.manager_id] = bucket;
      }

      setScanCounts(scanByMgr);
      setReviewAgg(revByMgr);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // A manager is treated as having an active QR if either the admin has
  // explicitly generated one (localStorage), or live data has shown up for
  // them (so the dashboard reflects reality even if no one clicked "Print").
  useEffect(() => {
    if (!isSuccess || !conditions?.managerList) return;
    const seeded = new Set(generated);
    let changed = false;
    for (const m of conditions.managerList) {
      if (scanCounts[m.UserID] || reviewAgg[m.UserID]) {
        if (!seeded.has(m.UserID)) {
          seeded.add(m.UserID);
          changed = true;
        }
      }
    }
    if (changed) setGenerated(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, conditions, scanCounts, reviewAgg]);

  const rows = useMemo(() => {
    if (!isSuccess) return [];
    return (conditions.managerList || []).map((m) => {
      const name =
        `${m.FirstName} ${m.LastName}`.trim() || m.Email || `Manager ${m.UserID}`;
      const code = (m.AirportCodes || [])[0] || "—";
      const hasQr = generated.has(m.UserID);

      const scans = scanCounts[m.UserID] || 0;
      const rev = reviewAgg[m.UserID];
      const reviews = rev?.count || 0;
      const rating = rev && rev.count > 0 ? rev.sum / rev.count : 0;

      return { ...m, name, code, hasQr, scans, reviews, rating };
    });
  }, [conditions, isSuccess, generated, scanCounts, reviewAgg]);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.scans - a.scans),
    [rows]
  );

  const stats = useMemo(() => {
    const total = rows.length;
    const withQr = rows.filter((r) => r.hasQr).length;
    const totalScans = rows.reduce((s, r) => s + r.scans, 0);
    const ratedRows = rows.filter((r) => r.reviews > 0);
    const avgRating =
      ratedRows.length > 0
        ? ratedRows.reduce((s, r) => s + r.rating, 0) / ratedRows.length
        : 0;
    return { total, withQr, withoutQr: total - withQr, totalScans, avgRating };
  }, [rows]);

  const topThree = useMemo(
    () => sortedRows.filter((r) => r.hasQr && r.scans > 0).slice(0, 3),
    [sortedRows]
  );

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const printQrFor = useCallback(async (row) => {
    const portalUrl = portalUrlForManager(row.UserID);
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=${encodeURIComponent(
      portalUrl
    )}`;

    let qrDataUrl;
    let logoDataUrl;
    try {
      [qrDataUrl, logoDataUrl] = await Promise.all([
        fetchAsDataURL(qrUrl),
        fetchAsDataURL(actLogo),
      ]);
    } catch (e) {
      window.alert(
        "Could not generate the QR image. Check your network connection and try again."
      );
      return;
    }

    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = pdf.internal.pageSize.getWidth();

    // Logo — centered at the top. Native ratio is 844×263 (~3.21:1).
    const logoH = 50;
    const logoW = (logoH * 844) / 263;
    pdf.addImage(logoDataUrl, "PNG", (pageW - logoW) / 2, 36, logoW, logoH);

    // Airport code — large, centered, below the logo
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(72);
    pdf.setTextColor(20);
    pdf.text(row.code, pageW / 2, 180, { align: "center" });

    // QR image
    const qrSize = 380;
    const qrX = (pageW - qrSize) / 2;
    const qrY = 220;
    pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

    // Tagline
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor(20);
    pdf.text(
      "Scan here to get information on future rides",
      pageW / 2,
      qrY + qrSize + 56,
      { align: "center" }
    );

    // Sub-tagline
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(14);
    pdf.setTextColor(90);
    pdf.text(
      "View today's schedule, leave feedback, and rate your driver.",
      pageW / 2,
      qrY + qrSize + 80,
      { align: "center" }
    );

    // URL footer
    pdf.setFontSize(11);
    pdf.setTextColor(140);
    pdf.text(portalUrl, pageW / 2, qrY + qrSize + 110, {
      align: "center",
    });

    const fileName = `qr-${row.code}-${row.UserID}.pdf`;
    const blobUrl = pdf.output("bloburl");
    const win = window.open(blobUrl, "_blank");
    if (!win) {
      // Pop-up blocked — fall back to direct download
      pdf.save(fileName);
    }
  }, []);

  const handleGenerateAndPrint = useCallback(
    async (row) => {
      setGenerated((prev) => {
        const next = new Set(prev);
        next.add(row.UserID);
        return next;
      });
      await printQrFor(row);
    },
    [printQrFor]
  );

  // Build a paged PDF of the manager's month of hotel-departure pickups,
  // formatted exactly like the crew QR portal: per-day sections with paired
  // pickups shown on a single merged time (midpoint of the 2 flights), the
  // manager's CrewTimeAdjustments applied, and the same 15-min / 12-pax
  // pairing rules. Opens in a new tab so it can be printed or saved.
  const printMonthlyScheduleFor = useCallback(async (row) => {
    const monthStr = window.prompt(
      "Which month? (YYYY-MM)",
      dayjs().format("YYYY-MM")
    );
    if (!monthStr) return;
    const monthStart = dayjs(`${monthStr}-01`);
    if (!monthStart.isValid()) {
      window.alert("That doesn't look like a valid YYYY-MM. Try again.");
      return;
    }
    const monthEnd = monthStart.endOf("month");

    // The crew portal only ever asks for one day; a full month of the same
    // joined view times out. Slice the month into 7-day chunks (exclusive
    // upper bound) and pull sequentially — each chunk stays well inside the
    // statement timeout.
    const schedules = [];
    const monthEndExclusive = monthEnd.add(1, "day").startOf("day");
    let cursor = monthStart.startOf("day");
    while (cursor.isBefore(monthEndExclusive)) {
      const chunkEnd = cursor.add(7, "day").isAfter(monthEndExclusive)
        ? monthEndExclusive
        : cursor.add(7, "day");
      const { data, error: err } = await supabase
        .from("ScheduleMixed")
        .select(
          `
          ScheduleID, ScheduleDate, PickupDateTime, IsOneOff, FlightNo,
          AirlineID, AirlineLetterCode, ManagerID,
          TransportationType: TransportationTypeID (Code),
          ScheduleStatus: ScheduleStatusID (Code),
          PickupLocation: pickuplocationid (LocationID, Description, Code, LocationTypeID),
          DropoffLocation: dropofflocationid (LocationID, Description, Code, LocationTypeID),
          SchedulePickups (Passengers)
          `
        )
        .eq("ManagerID", row.UserID)
        .gte("ScheduleDate", cursor.format("YYYY-MM-DD 00:00:00"))
        .lt("ScheduleDate", chunkEnd.format("YYYY-MM-DD 00:00:00"))
        .order("ScheduleDate", { ascending: true });
      if (err) {
        window.alert(`Couldn't load schedules: ${err.message}`);
        return;
      }
      schedules.push(...(data || []));
      cursor = chunkEnd;
    }

    const { data: adjustments } = await supabase
      .from("CrewTimeAdjustments")
      .select("start_minutes, end_minutes, adjustment_minutes")
      .eq("manager_id", row.UserID);

    const airportCode = (row.code || "").toUpperCase();
    // Adjustment window matching runs on the AIRPORT'S local hour so an
    // ONT 4-6am window fires on actual Pacific morning flights regardless
    // of the printing browser's timezone.
    const matchingAdjustment = (scheduleTime) => {
      if (!adjustments || adjustments.length === 0) return 0;
      const { hour, minute } = localHourMinute(scheduleTime.toDate(), airportCode);
      const minutes = hour * 60 + minute;
      for (const a of adjustments) {
        const lo = a.start_minutes ?? 0;
        const hi = a.end_minutes ?? 1440;
        if (minutes >= lo && minutes < hi) return a.adjustment_minutes || 0;
      }
      return 0;
    };

    const toTrip = (r) => {
      const pickupCode = (r.PickupLocation?.Code || "").toUpperCase();
      const dropoffCode = (r.DropoffLocation?.Code || "").toUpperCase();
      const pickupIsAirport = r.PickupLocation?.LocationTypeID === 2;
      const dropoffIsAirport = r.DropoffLocation?.LocationTypeID === 2;
      const isCrossStation =
        pickupIsAirport && dropoffIsAirport && pickupCode !== dropoffCode;
      const transportCode = (r.TransportationType?.Code || "").toUpperCase();
      let type;
      if (dropoffCode === airportCode) type = "depart";
      else if (isCrossStation && pickupCode === airportCode) type = "depart";
      else if (pickupCode === airportCode) type = "arrive";
      else if (transportCode === "DEPART") type = "depart";
      else type = "arrive";

      const isJetBlue = (r.AirlineLetterCode || "").toUpperCase() === "B6";
      const baseRaw = isJetBlue
        ? r.PickupDateTime || r.ScheduleDate
        : r.ScheduleDate;
      const scheduleTime = dayjs(baseRaw);
      const adjMinutes =
        !r.IsOneOff && !isJetBlue && scheduleTime.isValid()
          ? matchingAdjustment(scheduleTime)
          : 0;
      const time = scheduleTime.isValid()
        ? scheduleTime.subtract(adjMinutes, "minute")
        : scheduleTime;
      return {
        id: r.ScheduleID,
        sortKey: time.isValid() ? time.valueOf() : 0,
        time,
        airlineCode: r.AirlineLetterCode || "",
        flightNo: r.FlightNo
          ? `${r.AirlineLetterCode || ""} ${r.FlightNo}`.trim()
          : r.AirlineLetterCode || "",
        type,
        pickup: r.PickupLocation?.Description || pickupCode || "—",
        dropoff: r.DropoffLocation?.Description || dropoffCode || "—",
        statusCode: (r.ScheduleStatus?.Code || "").toUpperCase(),
        passengers: r.SchedulePickups?.[0]?.Passengers || 0,
      };
    };

    const PAIR_WINDOW_MS = 15 * 60 * 1000;
    const PAIR_MAX_CREW = 12;
    const MCO_SW_MAX_CREW = 10;
    const pairCap = (a, b) =>
      airportCode === "MCO" && a.airlineCode === "WN" && b.airlineCode === "WN"
        ? MCO_SW_MAX_CREW
        : PAIR_MAX_CREW;
    // Vans pick up from ONE hotel per run — mirror the QR portal's rule
    // that only merges close-time trips if they share the same pickup hotel.
    // Different hotels (e.g. ONT Mission Inn vs Marriott) render as separate
    // rows even when 15 min apart.
    const sameHotel = (a, b) => {
      const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
      return !!a && !!b && norm(a) === norm(b);
    };
    const groupClose = (trips) => {
      const sorted = [...trips].sort((x, y) => x.sortKey - y.sortKey);
      const groups = [];
      for (let i = 0; i < sorted.length; ) {
        const a = sorted[i];
        const b = sorted[i + 1];
        const within = b && b.sortKey - a.sortKey <= PAIR_WINDOW_MS;
        const cap = b ? pairCap(a, b) : PAIR_MAX_CREW;
        const okPax = b && (a.passengers || 0) + (b.passengers || 0) <= cap;
        const okHotel = b && sameHotel(a.pickup, b.pickup);
        if (within && okPax && okHotel) {
          groups.push({
            trips: [a, b],
            mergedTime: dayjs(Math.floor((a.sortKey + b.sortKey) / 2)),
          });
          i += 2;
        } else {
          groups.push({ trips: [a], mergedTime: a.time });
          i += 1;
        }
      }
      return groups;
    };

    // Bucket trips by the AIRPORT'S local calendar day so a Pacific flight
    // late at night doesn't slide onto the next day just because the ET
    // clock has already rolled over.
    const dayInAirportTz = (d) => {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: (airportCode === "PHX" || airportCode === "MZJ") ? "America/Phoenix" :
                  (airportCode === "SJU") ? "America/Puerto_Rico" :
                  ["BUR","LAS","LAX","LGB","LSV","OAK","ONT","PDX","RNO","SAN","SEA","SFO","SJC","SMF","SNA","STS"].includes(airportCode) ? "America/Los_Angeles" :
                  ["AEX","AFW","AUS","BNA","DAL","DFW","GGG","HOU","IAH","MCI","MDW","MEM","MKE","MLU","MSP","MSY","OKC","ORD","STL","TUL","VPS"].includes(airportCode) ? "America/Chicago" :
                  ["ABQ","COS","DEN","ELP"].includes(airportCode) ? "America/Denver" :
                  "America/New_York",
        year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(d);
      return (parts.find((p) => p.type === "year")?.value || "") + "-" +
             (parts.find((p) => p.type === "month")?.value || "") + "-" +
             (parts.find((p) => p.type === "day")?.value || "");
    };
    const departsByDay = {};
    for (const r of schedules || []) {
      const t = toTrip(r);
      if (t.type !== "depart") continue;
      if (t.statusCode === "CANCELLED" || t.statusCode === "CXL") continue;
      // Soft-deleted trips stay on record for reports/audit but shouldn't
      // print on the crew's van schedule.
      if (t.statusCode === "DELETED") continue;
      const day = dayInAirportTz(t.time.toDate());
      if (!departsByDay[day]) departsByDay[day] = [];
      departsByDay[day].push(t);
    }

    // Build the PDF — one continuous document, page-breaks as needed.
    let logoDataUrl;
    try {
      logoDataUrl = await fetchAsDataURL(actLogo);
    } catch {
      logoDataUrl = null;
    }
    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const marginX = 36;
    let y = 40;

    const drawHeader = () => {
      if (logoDataUrl) {
        const logoH = 28;
        const logoW = (logoH * 844) / 263;
        pdf.addImage(logoDataUrl, "PNG", marginX, y, logoW, logoH);
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(20);
      pdf.text(
        `${airportCode} — Crew Schedule`,
        pageW - marginX,
        y + 18,
        { align: "right" }
      );
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(90);
      pdf.text(
        monthStart.format("MMMM YYYY"),
        pageW - marginX,
        y + 34,
        { align: "right" }
      );
      y += 55;
      pdf.setDrawColor(200);
      pdf.line(marginX, y, pageW - marginX, y);
      y += 12;
    };

    const ensureRoom = (needed) => {
      if (y + needed > pageH - 40) {
        pdf.addPage();
        y = 40;
        drawHeader();
      }
    };

    drawHeader();

    const days = Object.keys(departsByDay).sort();
    if (days.length === 0) {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(12);
      pdf.setTextColor(120);
      pdf.text(
        `No hotel-departure pickups on file for ${monthStart.format("MMMM YYYY")}.`,
        marginX,
        y + 20
      );
    }
    for (const day of days) {
      const groups = groupClose(departsByDay[day]);
      // Day header
      ensureRoom(28);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.setTextColor(0);
      pdf.text(dayjs(day).format("dddd, MMMM D"), marginX, y);
      y += 14;
      pdf.setDrawColor(230);
      pdf.line(marginX, y, pageW - marginX, y);
      y += 8;

      // Column layout — wider FLIGHT column so long flight strings don't
      // spill into HOTEL; PAX column removed at the user's request. Hotel
      // gets the leftover width so long hotel names read cleanly.
      const colTimeX = marginX;
      const colFlightX = marginX + 100;
      const colHotelX = marginX + 260;
      const colRightX = pageW - marginX;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      pdf.text("PICKUP", colTimeX, y);
      pdf.text("FLIGHT", colFlightX, y);
      pdf.text("HOTEL", colHotelX, y);
      y += 14;

      const stripAdhoc = (fn) => String(fn || "").replace(/-ADHOC/gi, "").trim();
      const LINE_H = 15;

      for (const g of groups) {
        // Format all times in the AIRPORT'S local timezone so the printed
        // schedule reads in the same clock the crews and on-site drivers
        // actually work in.
        const timeStr = formatInAirportTz(g.mergedTime.toDate(), airportCode);
        const rowH = LINE_H * g.trips.length + 8;
        ensureRoom(rowH + 8);
        // Time (large, left) — vertically centered against the first trip line.
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(20);
        pdf.text(timeStr, colTimeX, y + 2);
        // Trips stacked
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        for (let i = 0; i < g.trips.length; i++) {
          const t = g.trips[i];
          const rowY = y + i * LINE_H;
          const individualTime = formatInAirportTz(t.time.toDate(), airportCode);
          const flightStr = `${stripAdhoc(t.flightNo)}   (${individualTime})`;
          pdf.setTextColor(40);
          pdf.text(flightStr, colFlightX, rowY + 2);
          // Hotel — clamp width to the space between HOTEL col and right margin
          const hotel = String(t.pickup || "");
          const maxHotelW = colRightX - colHotelX - 4;
          let hotelStr = hotel;
          if (pdf.getTextWidth(hotelStr) > maxHotelW) {
            while (pdf.getTextWidth(hotelStr + "…") > maxHotelW && hotelStr.length > 0)
              hotelStr = hotelStr.slice(0, -1);
            hotelStr += "…";
          }
          pdf.text(hotelStr, colHotelX, rowY + 2);
        }

        y += rowH;
        // Separator between groups
        pdf.setDrawColor(235);
        pdf.line(marginX, y - 4, pageW - marginX, y - 4);
      }
      y += 12;
    }

    const fileName = `${airportCode}-${monthStart.format("YYYY-MM")}-schedule.pdf`;
    const blobUrl = pdf.output("bloburl");
    const win = window.open(blobUrl, "_blank");
    if (!win) pdf.save(fileName);
  }, []);

  const handleRevoke = useCallback((userId) => {
    setGenerated((prev) => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  }, []);

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      <div className="items-center mt-5">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>

        <div className="pt-4 w-full flex justify-between items-center">
          <p className="font-semibold text-lg flex items-center gap-2">
            <FaQrcode /> Generate QR — Crew Portal
          </p>
        </div>

        <p className="text-gray-600 mt-2 max-w-3xl">
          Each manager gets a unique QR code for their van. The QR encodes a
          link tied to that manager — when pilots scan it, the crew portal
          loads every schedule assigned to that manager (filtered by today's
          date), so the codes always stay in sync with whatever is imported
          for their location.
        </p>

        {/* Top stats */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="departure-custom-card rounded-lg">
            <p className="text-gray-500">Managers</p>
            <p className="text-lg font-semibold">{stats.total}</p>
          </div>
          <div className="departure-custom-card rounded-lg">
            <p className="text-gray-500">Active QR codes</p>
            <p className="text-lg font-semibold">
              {stats.withQr}{" "}
              <span className="text-sm text-gray-400">
                / {stats.total}
              </span>
            </p>
          </div>
          <div className="departure-custom-card rounded-lg">
            <p className="text-gray-500">Total scans (all-time)</p>
            <p className="text-lg font-semibold">
              {stats.totalScans.toLocaleString()}
            </p>
          </div>
          <div className="departure-custom-card rounded-lg">
            <p className="text-gray-500">Avg pilot rating</p>
            <p className="text-lg font-semibold">
              {stats.avgRating ? stats.avgRating.toFixed(2) : "—"}
            </p>
          </div>
        </div>

        {/* Top users */}
        {topThree.length > 0 && (
          <div className="mt-6">
            <p className="text-location flex items-center gap-2">
              <FaTrophy className="text-yellow-500" /> Most-scanned managers
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
              {topThree.map((r, idx) => (
                <div
                  key={`top-${r.UserID}`}
                  className="departure-custom-card rounded-lg flex items-center justify-between"
                >
                  <div>
                    <p className="text-gray-500">#{idx + 1}</p>
                    <p className="text-lg font-semibold flex items-center gap-2">
                      {r.name} <AirportPill code={r.code} />
                    </p>
                    {r.reviews > 0 ? (
                      <Stars value={r.rating} />
                    ) : (
                      <span className="text-xs text-gray-400">No reviews yet</span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold">{r.scans}</p>
                    <p className="text-xs text-gray-500">scans</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pickup time adjustments */}
        <TimeAdjustmentsContainer
          managers={conditions?.managerList || []}
          isLoadingManagers={!isSuccess}
        />

        {/* Full table */}
        <div className="mt-6">
          <p className="text-location">All managers</p>
          <div className="rounded-lg overflow-hidden border mt-2">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left p-3">Manager</th>
                  <th className="text-left p-3">QR status</th>
                  <th className="text-left p-3">Pilot rating</th>
                  <th className="text-left p-3">Scans (30d)</th>
                  <th className="text-left p-3">Reviews</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {!isSuccess && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      Loading managers…
                    </td>
                  </tr>
                )}
                {isSuccess && sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      No managers found.
                    </td>
                  </tr>
                )}
                {sortedRows.map((r) => (
                  <tr key={r.UserID} className="border-t">
                    <td className="p-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {r.name}
                        <AirportPill code={r.code} />
                      </span>
                    </td>
                    <td className="p-3">
                      {r.hasQr ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <FaCheckCircle /> Active
                          </span>
                          <a
                            href={portalUrlForManager(r.UserID)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-500 hover:text-blue-600 break-all"
                          >
                            {portalUrlForManager(r.UserID)}
                          </a>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400">
                          <FaRegCircle /> Not generated
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {r.hasQr && r.reviews > 0 ? (
                        <button
                          type="button"
                          onClick={() => setReviewsModal(r)}
                          className="inline-flex items-center gap-2 hover:underline cursor-pointer bg-transparent border-0 p-0"
                          title="View individual reviews"
                        >
                          <Stars value={r.rating} />
                          <span className="text-xs text-gray-500">
                            ({r.rating.toFixed(1)})
                          </span>
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {r.hasQr ? (
                        r.scans.toLocaleString()
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {r.hasQr ? (
                        r.reviews > 0 ? (
                          <button
                            type="button"
                            onClick={() => setReviewsModal(r)}
                            className="hover:underline cursor-pointer bg-transparent border-0 p-0 text-blue-600"
                            title="View individual reviews"
                          >
                            {r.reviews}
                          </button>
                        ) : (
                          r.reviews
                        )
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {r.hasQr ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => printQrFor(r)}
                            className="custom-button rounded-lg px-3 py-1 inline-flex items-center gap-2 border-none"
                          >
                            <FaPrint /> Print
                          </button>
                          <button
                            onClick={() => printMonthlyScheduleFor(r)}
                            className="custom-white-button rounded-lg px-3 py-1 inline-flex items-center gap-2"
                            title="Download the whole month's pickup schedule in QR format"
                          >
                            <FaPrint /> Print QR Schedule
                          </button>
                          <button
                            onClick={() => handleRevoke(r.UserID)}
                            className="custom-white-button rounded-lg px-3 py-1 inline-flex items-center gap-2"
                            title="Revoke QR (mockup)"
                          >
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleGenerateAndPrint(r)}
                          className="custom-blue-button rounded-lg px-3 py-1 inline-flex items-center gap-2 border-none"
                        >
                          <FaQrcode /> Print QR code
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {reviewsModal && (
        <ReviewsModal
          manager={reviewsModal}
          onClose={() => setReviewsModal(null)}
        />
      )}
    </div>
  );
};

// ============================================================
// Reviews drill-down modal
// ============================================================

const ReviewsModal = ({ manager, onClose }) => {
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);
    supabase
      .from("CrewReviews")
      .select("id, rating, comment, pilot_name, pilot_id, flight_number, created_at")
      .eq("manager_id", manager.UserID)
      .order("created_at", { ascending: false })
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) {
          setError(err.message);
          setStatus("error");
          return;
        }
        setReviews(data || []);
        setStatus("ready");
      });
    return () => {
      cancelled = true;
    };
  }, [manager.UserID]);

  // Close on Escape so the modal feels native.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container h-auto"
        style={{ width: "640px", maxWidth: "92vw" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header mb-0">
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="w-full flex flex-col gap-2">
          <p className="text-lg font-semibold mb-0 flex items-center gap-2">
            {manager.name}
            <AirportPill code={manager.code} />
          </p>
          <p className="text-gray-500 text-sm">
            {manager.reviews} review{manager.reviews === 1 ? "" : "s"} · average{" "}
            {manager.rating.toFixed(1)} / 5
          </p>
          <div
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              marginTop: "8px",
            }}
          >
            {status === "loading" && (
              <div className="text-center text-gray-500 py-6">
                Loading reviews…
              </div>
            )}
            {status === "error" && (
              <div className="text-red-600 text-sm">
                Couldn't load reviews: {error}
              </div>
            )}
            {status === "ready" && reviews.length === 0 && (
              <div className="text-center text-gray-500 py-6">
                No reviews yet.
              </div>
            )}
            {status === "ready" && reviews.length > 0 && (
              <ul className="flex flex-col gap-3">
                {reviews.map((rv) => (
                  <li
                    key={rv.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Stars value={rv.rating} />
                      <span className="text-xs text-gray-500">
                        {dayjs(rv.created_at).format("MMM D, YYYY h:mm A")}
                      </span>
                    </div>
                    {(rv.pilot_name || rv.pilot_id || rv.flight_number) && (
                      <p
                        className="text-xs text-gray-500"
                        style={{ margin: "0 0 6px 0" }}
                      >
                        {[
                          rv.pilot_name,
                          rv.pilot_id && `ID: ${rv.pilot_id}`,
                          rv.flight_number && `Flight: ${rv.flight_number}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                    {rv.comment ? (
                      <p
                        className="text-sm text-gray-700"
                        style={{ whiteSpace: "pre-wrap", margin: 0 }}
                      >
                        {rv.comment}
                      </p>
                    ) : (
                      <p
                        className="text-sm text-gray-400"
                        style={{ fontStyle: "italic", margin: 0 }}
                      >
                        No comment left.
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Pickup time adjustments
// ============================================================

const EMPTY_FORM = {
  id: null,
  manager_id: "",
  start: "",
  end: "",
  adjustment_minutes: "",
};

// minutes-of-day <-> "HH:MM" helpers for the form inputs. The DB stores
// minutes-since-midnight as a smallint; the <input type="time"> widget gives
// us a string like "06:00".
const minutesToTime = (m) => {
  if (m == null) return "";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const timeToMinutes = (s) => {
  if (!s) return null;
  const [h, m] = s.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const formatWindow = (a) => {
  if (a.start_minutes == null && a.end_minutes == null) return "All day";
  const from = a.start_minutes == null ? "Start of day" : fmtClock(a.start_minutes);
  const to = a.end_minutes == null ? "End of day" : fmtClock(a.end_minutes);
  return `${from} – ${to}`;
};

const fmtClock = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
};

const TimeAdjustmentsContainer = ({ managers, isLoadingManagers }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const managerById = useMemo(() => {
    const m = {};
    for (const r of managers) m[r.UserID] = r;
    return m;
  }, [managers]);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from("CrewTimeAdjustments")
      .select("id, manager_id, start_minutes, end_minutes, adjustment_minutes")
      .order("manager_id", { ascending: true })
      .order("start_minutes", { ascending: true, nullsFirst: true });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setError(null);
    setRows(data || []);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const grouped = useMemo(() => {
    const rowsWithManager = rows.map((r) => ({
      ...r,
      manager: managerById[r.manager_id],
    }));
    return rowsWithManager.sort((a, b) => {
      const aCode = a.manager?.AirportCodes?.[0] || "";
      const bCode = b.manager?.AirportCodes?.[0] || "";
      if (aCode !== bCode) return aCode.localeCompare(bCode);
      return (a.start_minutes ?? -1) - (b.start_minutes ?? -1);
    });
  }, [rows, managerById]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      manager_id: row.manager_id,
      start: minutesToTime(row.start_minutes),
      end: minutesToTime(row.end_minutes),
      adjustment_minutes: String(row.adjustment_minutes),
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  const submitForm = async () => {
    if (!form.manager_id) {
      setError("Pick a manager.");
      return;
    }
    const adj = Number(form.adjustment_minutes);
    if (!Number.isFinite(adj) || adj < 0) {
      setError("Adjustment must be a non-negative number of minutes.");
      return;
    }
    const start = timeToMinutes(form.start);
    const end = timeToMinutes(form.end);
    if (start != null && end != null && start >= end) {
      setError("Start time must be before end time.");
      return;
    }

    setSaving(true);
    setError(null);
    const payload = {
      manager_id: form.manager_id,
      start_minutes: start,
      end_minutes: end,
      adjustment_minutes: adj,
    };
    const { error: err } = form.id
      ? await supabase
          .from("CrewTimeAdjustments")
          .update(payload)
          .eq("id", form.id)
      : await supabase.from("CrewTimeAdjustments").insert(payload);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    cancelForm();
    reload();
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete adjustment for ${row.manager?.AirportCodes?.[0] || "this manager"} (${formatWindow(row)} − ${row.adjustment_minutes} min)?`
      )
    )
      return;
    const { error: err } = await supabase
      .from("CrewTimeAdjustments")
      .delete()
      .eq("id", row.id);
    if (err) {
      setError(err.message);
      return;
    }
    reload();
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <p className="text-location flex items-center gap-2">
          <FaClock /> Pickup time adjustments
        </p>
        {!showForm && (
          <button
            onClick={openAdd}
            disabled={isLoadingManagers}
            className="custom-blue-button rounded-lg px-3 py-1 inline-flex items-center gap-2 border-none"
          >
            <FaPlus /> Add adjustment
          </button>
        )}
      </div>

      <p className="text-gray-600 text-sm mt-1 max-w-3xl">
        The crew portal, admin schedule view, and downloaded reports all show
        the schedule time minus this adjustment for hotel→airport pickups.
        The underlying schedule data is never modified. Use a window
        (e.g. 6:00 AM – 11:59 PM) when a location needs different offsets at
        different times of day; leave both empty to apply all day.
      </p>
      <p className="text-gray-600 text-sm mt-1 max-w-3xl italic">
        JetBlue (B6) flights always display the schedule time as-is — these
        adjustments are skipped for B6 regardless of manager or window.
      </p>

      {error && (
        <div className="mt-3 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {showForm && (
        <div className="mt-3 p-4 rounded-lg border bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="flex flex-col text-sm">
              <span className="text-gray-600 mb-1">Manager</span>
              <select
                value={form.manager_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, manager_id: e.target.value }))
                }
                className="border rounded px-2 py-1"
              >
                <option value="">Select manager…</option>
                {managers.map((m) => {
                  const code = (m.AirportCodes || [])[0] || "—";
                  const name =
                    `${m.FirstName} ${m.LastName}`.trim() || m.Email || `Manager ${m.UserID}`;
                  return (
                    <option key={m.UserID} value={m.UserID}>
                      {code} · {name}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="flex flex-col text-sm">
              <span className="text-gray-600 mb-1">Start (optional)</span>
              <input
                type="time"
                value={form.start}
                onChange={(e) =>
                  setForm((f) => ({ ...f, start: e.target.value }))
                }
                className="border rounded px-2 py-1"
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="text-gray-600 mb-1">End (optional)</span>
              <input
                type="time"
                value={form.end}
                onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                className="border rounded px-2 py-1"
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="text-gray-600 mb-1">Adjustment (min earlier)</span>
              <input
                type="number"
                min="0"
                step="1"
                value={form.adjustment_minutes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, adjustment_minutes: e.target.value }))
                }
                className="border rounded px-2 py-1"
              />
            </label>
            <div className="flex gap-2">
              <button
                onClick={submitForm}
                disabled={saving}
                className="custom-blue-button rounded-lg px-3 py-1 inline-flex items-center gap-2 border-none"
              >
                <FaSave /> {saving ? "Saving…" : form.id ? "Save" : "Add"}
              </button>
              <button
                onClick={cancelForm}
                className="custom-white-button rounded-lg px-3 py-1 inline-flex items-center gap-2"
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg overflow-hidden border mt-3">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left p-3">Airport</th>
              <th className="text-left p-3">Manager</th>
              <th className="text-left p-3">Window</th>
              <th className="text-left p-3">Adjustment</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  Loading adjustments…
                </td>
              </tr>
            )}
            {!loading && grouped.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  No adjustments configured yet.
                </td>
              </tr>
            )}
            {!loading &&
              grouped.map((r) => {
                const code = r.manager?.AirportCodes?.[0] || "—";
                const name = r.manager
                  ? `${r.manager.FirstName} ${r.manager.LastName}`.trim() ||
                    r.manager.Email ||
                    `Manager ${r.manager_id}`
                  : `Manager ${r.manager_id}`;
                return (
                  <tr key={r.id} className="border-t">
                    <td className="p-3">
                      <AirportPill code={code} />
                    </td>
                    <td className="p-3">{name}</td>
                    <td className="p-3">{formatWindow(r)}</td>
                    <td className="p-3 font-medium">
                      −{r.adjustment_minutes} min
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="custom-white-button rounded-lg px-3 py-1 inline-flex items-center gap-2"
                        >
                          <FaPen /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(r)}
                          className="custom-white-button rounded-lg px-3 py-1 inline-flex items-center gap-2 text-red-600"
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QRCodes;
